import React, { useContext } from "react";
import { Helmet } from "react-helmet";
import { cn, imageBuilder, toPlainText } from "../lib/helpers";
import { Badge, GetStarted, PageHeader, SectionWrapper } from "./sections";
import PortableText from "./portableText";
import { CardGrid } from "./card/card-grid";
import { SiteContext } from "./global/site-context";
import { Byline } from "./card/byline";
import { CardContext } from "./card/context";
import { TableOfContents } from "./sections/table-of-contents";
import { pageDocPath } from "../../helpers";
import { MAX_SUGGESTED_POSTS } from "../lib/suggestions";
import { ImageBuilder } from "./global/image-builder";
import { SectionContext } from "./sections/context";
import { BackToZone } from "./back-to-zone";
import { ProductDisclaimer } from "./product-disclaimer";

import * as globalStyles from "./global/global.module.css";

export const PlaybookPage = ({
  _id,
  title,
  _rawExcerpt,
  integrations,
  suggestions,
  _rawBody,
  authors,
  teams,
  skill,
  _rawTakeAways,
  offerings,
  suggest,
  categories,
  _createdAt,
  publishedAt,
}) => {
  const site = useContext(SiteContext);
  // @todo standardize with page setting
  const isHelloPage = site.doc.slug.current.match(/hello\//);
  const hasBanner = site.banners.length > 0;

  return (
    <>
      <SectionWrapper
        {...{
          noHeaders: true,
          background: "mint",
          name: "header",
          backgroundCentered: true,
          container: "large",
          className: "overflow-hidden relative",
        }}
      >
        {!isHelloPage && <BackToZone to="/resources/playbooks/" text="See all playbooks" />}
        {title && (
          <PageHeader
            section={{
              header: title,
              _rawSummary: _rawExcerpt,
            }}
            detailsBox={
              <div className="relative">
                <div
                  className={cn(
                    "absolute -inset-8",
                    globalStyles.radialHighlight
                  )}
                  style={{ opacity: 0.7 }}
                ></div>
                {_rawTakeAways ? (
                  <div className="bg-white p-6 rounded-xl relative">
                    <PortableText blocks={_rawTakeAways} />
                  </div>
                ) : (
                  <div className="bg-white p-6 rounded-xl relative">
                    <p className="text-secondary font-medium text-sm mb-2">
                      Teams:
                    </p>
                    <p className="font-semibold">{teams.join(", ")}</p>
                    <p className="text-secondary font-medium text-sm mt-6 mb-2">
                      Sources:
                    </p>
                    <p>
                      <IntegrationsTiles integrations={integrations} />
                    </p>
                  </div>
                )}
              </div>
            }
          />
        )}
        <ProductDisclaimer
          categories={categories}
          _createdAt={_createdAt}
          publishedAt={publishedAt}
        />
      </SectionWrapper>

      {site.hideNav && (
        <SectionWrapper padding="none">
          <Badge />
        </SectionWrapper>
      )}

      {authors.length > 0 && (
        <SectionWrapper padding="top" container="large">
          <div className="pt-3 pb-6 md:pt-0">
            <CardContext.Provider value={{ authors }}>
              <Byline />
            </CardContext.Provider>
          </div>
        </SectionWrapper>
      )}

      <SectionWrapper container="large">
        <TableOfContentsSection
          {...{ _rawBody }}
          offering={offerings?.find((o) => o?.status === "published")}
        />
      </SectionWrapper>

      <SectionWrapper
        anchor="more"
        container="large"
        header={site.token("suggestions")}
        background="lavenderUltraLight"
      >
        {suggestions.length > 0 ? (
          <CardGrid
            nodes={suggestions}
            panels={true}
            showSummary={true}
            makeThumbnail={true}
            browseMoreHref="/playbooks/"
            browseMoreText="See all playbooks"
            attribution={{ action: "suggestion", label: "playbook" }}
            maxColumns={3}
          />
        ) : (
          <CardGrid
            nodes={suggest.slice(0, MAX_SUGGESTED_POSTS)}
            browseMoreHref="/playbooks/"
            browseMoreText="See all playbooks"
            makeThumbnail={true}
            showSummary={true}
            maxColumns={3}
            allowSummary={true}
            panels={true}
            attribution={{ action: "suggestion" }}
          />
        )}
      </SectionWrapper>

      <SectionWrapper container="large" background="mint" name="getStarted">
        <GetStarted />
      </SectionWrapper>
    </>
  );
};

export const TableOfContentsSection = ({ _rawBody, offering }) => (
  <div className="grid md:grid-cols-1/3-2/3 relative">
    <div className="md:mr-12">
      <div className="sticky top-36 left-0" style={{ maxWidth: 370 }}>
        <TableOfContents blocks={_rawBody} offering={offering} />
      </div>
    </div>
    <div className="">{_rawBody && <PortableText blocks={_rawBody} />}</div>
  </div>
);

const generateIntegrationsListSchema = (integrations) => {
  if (!integrations || integrations.length === 0) return null;

  const schema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": integrations.map((int, index) => {
      const item = {
        "@type": "SoftwareApplication",
        "name": int.company,
        "applicationCategory": int.category || "BusinessApplication",
      };

      if (int.mainImage?.asset) {
        item.image = imageBuilder(int.mainImage)?.height(200)?.url();
      }

      if (int._rawExcerpt) {
        item.offers = toPlainText(int._rawExcerpt);
      }

      return {
        "@type": "ListItem",
        "position": index + 1,
        "item": item,
      };
    }),
  };

  return schema;
};

export const IntegrationsTiles = ({ integrations, unlinked, untitled }) => {
  const site = useContext(SiteContext);
  const section = useContext(SectionContext);
  const linked = !unlinked && site.isActive("linkIntegrations");
  const integrationsListSchema = generateIntegrationsListSchema(integrations);

  return (
    <>
      {integrationsListSchema && (
        <Helmet
          script={[{
            type: "application/ld+json",
            innerHTML: JSON.stringify(integrationsListSchema),
          }]}
        />
      )}
      {integrations.map((integration) => {
        const Tag = linked ? "a" : "span";
        return (
          <Tag
            href={linked && integration.docRef ? pageDocPath(integration.docRef) : undefined}
            className={cn(
              "inline-block whitespace-nowrap font-medium",
              linked ? "mr-4 mb-1 transform scale-100 hover:scale-105 transition-card duration-200" : null,
              section.isDarkBg ? "bg-white rounded-md p-1 pb-0.5 flex min-h-7 items-center" : null
            )}
            title={integration.company}
            key={integration._id}
          >
            <ImageBuilder
              image={integration.mainImage}
              width={20}
              className={cn(
                "inline-block relative -top-px",
                !unlinked && !untitled && integration.company ? "mr-1.5" : null
              )}
              alt={`${integration.company} logo`}
            />
            {!unlinked && !untitled && integration.company}
          </Tag>
        );
      })}
    </>
  );
};
